import { ProjectIntegrationProviderName } from "@/store/integrations/integrations-selectors";
import { orange, red } from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import {
  ExternalMarkupIElement,
  IElementType,
  WorkspaceIntegrationProviderId,
} from "@faro-lotv/ielement-types";
import {
  BcfServicesApiClient,
  BcfServicesIntegrationType,
  CollaborationUser,
  GenericUserInfo,
  GetTopicDocumentReference,
} from "@faro-lotv/service-wires";
import {
  ExternalAnnotationError,
  ExternalAnnotationErrorType,
} from "./external-annotation-types";

/**
 * Represents the names of different types of external annotations
 */
enum ExternalAnnotationTypeName {
  /** For external annotation of type Issue */
  Issue = "Issue",
  /** For external annotation of type RFI */
  Rfi = "RFI",
  /** For external annotation of type Observation */
  Observation = "Observation",
}

type IntegrationsDataAssociations = {
  /** The "BCF Services" integration type */
  bcfIntegrationType: BcfServicesIntegrationType;
  /** The iElementType corresponding to the "BCF Services" integration type */
  iElementType: ExternalMarkupIElement["type"];
  /** The name of the project integration provider */
  providerName: ProjectIntegrationProviderName;
  /** The name of the annotation type */
  annotationTypeName: ExternalAnnotationTypeName;
  /** The ID of the workspace integration */
  integrationProviderId: WorkspaceIntegrationProviderId;
};

// The associations between "BCF Services" integration types and the other related data and types defined elsewhere
const integrationsDataAssociations: IntegrationsDataAssociations[] = [
  {
    bcfIntegrationType: BcfServicesIntegrationType.autodeskAccIssues,
    iElementType: IElementType.markupAccIssue,
    providerName: ProjectIntegrationProviderName.autodeskAcc,
    annotationTypeName: ExternalAnnotationTypeName.Issue,
    integrationProviderId: WorkspaceIntegrationProviderId.autodesk,
  },
  {
    bcfIntegrationType: BcfServicesIntegrationType.autodeskAccRfis,
    iElementType: IElementType.markupAccRfi,
    providerName: ProjectIntegrationProviderName.autodeskAcc,
    annotationTypeName: ExternalAnnotationTypeName.Rfi,
    integrationProviderId: WorkspaceIntegrationProviderId.autodesk,
  },
  {
    bcfIntegrationType: BcfServicesIntegrationType.autodeskBim360Issues,
    iElementType: IElementType.markupBim360,
    providerName: ProjectIntegrationProviderName.autodeskBim360,
    annotationTypeName: ExternalAnnotationTypeName.Issue,
    integrationProviderId: WorkspaceIntegrationProviderId.autodesk,
  },
  {
    bcfIntegrationType: BcfServicesIntegrationType.procoreObservations,
    iElementType: IElementType.markupProcoreObservation,
    providerName: ProjectIntegrationProviderName.procore,
    annotationTypeName: ExternalAnnotationTypeName.Observation,
    integrationProviderId: WorkspaceIntegrationProviderId.procore,
  },
  {
    bcfIntegrationType: BcfServicesIntegrationType.procoreRfis,
    iElementType: IElementType.markupProcoreRfi,
    providerName: ProjectIntegrationProviderName.procore,
    annotationTypeName: ExternalAnnotationTypeName.Rfi,
    integrationProviderId: WorkspaceIntegrationProviderId.procore,
  },
];

/**
 * @param providerName The name of the integration provider
 * @returns all "BCF Services" integration types for the given project integration provider
 */
export function getAllBcfServicesIntegrationTypes(
  providerName: ProjectIntegrationProviderName,
): BcfServicesIntegrationType[] {
  return integrationsDataAssociations
    .filter((association) => association.providerName === providerName)
    .map((association) => association.bcfIntegrationType);
}

/**
 * @param bcfIntegrationType  The bcf integration type for which the we need the corresponding iElement type
 * @returns The iElement type corresponding to the given bcf integration type
 * @throws Will throw an error if the specified bcf integration type is not found.
 */
export function getIElementTypeForBcfIntegrationType(
  bcfIntegrationType: BcfServicesIntegrationType,
): ExternalMarkupIElement["type"] {
  const valueFound = integrationsDataAssociations.find(
    (association) => association.bcfIntegrationType === bcfIntegrationType,
  );
  assert(
    valueFound,
    `The bcf service integration type ${bcfIntegrationType} is not found`,
  );
  return valueFound.iElementType;
}

/**
 * @param iElementType The iElement type for which we need the corresponding BCF Services integration type
 * @returns The BCF Services integration type corresponding to the given iElement type
 * @throws Will throw an error if the specified iElement type is not found.
 */
export function getBcfIntegrationTypeForIElementType(
  iElementType: ExternalMarkupIElement["type"],
): BcfServicesIntegrationType {
  const valueFound = integrationsDataAssociations.find(
    (association) => association.iElementType === iElementType,
  );
  assert(valueFound, `The iElementType ${iElementType} is not found`);
  return valueFound.bcfIntegrationType;
}

/**
 * @param bcfIntegrationType - The BCF integration type for which to find the provider name.
 * @returns The provider name corresponding to the specified BCF integration type.
 * @throws Will throw an error if the specified BCF integration type is not found.
 */
export function getExternalAnnotationProviderName(
  bcfIntegrationType: BcfServicesIntegrationType,
): ProjectIntegrationProviderName {
  const valueFound = integrationsDataAssociations.find(
    (association) => association.bcfIntegrationType === bcfIntegrationType,
  );
  assert(
    valueFound,
    `The bcf service integration type ${bcfIntegrationType} is not found`,
  );
  return valueFound.providerName;
}

/**
 * @param bcfIntegrationType - The BCF integration type for which to find the annotation type name.
 * @returns The annotation type name corresponding to the specified BCF integration type.
 * @throws Will throw an error if the specified BCF integration type is not found.
 */
export function getExternalAnnotationTypeName(
  bcfIntegrationType: BcfServicesIntegrationType,
): ExternalAnnotationTypeName {
  const valueFound = integrationsDataAssociations.find(
    (association) => association.bcfIntegrationType === bcfIntegrationType,
  );
  assert(
    valueFound,
    `The bcf service integration type ${bcfIntegrationType} is not found`,
  );
  return valueFound.annotationTypeName;
}

/**
 * @param bcfIntegrationType The BCF integration type for which to find the workspace integration provider ID.
 * @returns The workspace integration provider ID corresponding to the specified BCF integration type.
 * @throws Will throw an error if the specified BCF integration type is not found.
 */
export function getWorkspaceIntegrationProviderId(
  bcfIntegrationType: BcfServicesIntegrationType,
): WorkspaceIntegrationProviderId {
  const valueFound = integrationsDataAssociations.find(
    (association) => association.bcfIntegrationType === bcfIntegrationType,
  );
  assert(
    valueFound,
    `The bcf service integration type ${bcfIntegrationType} is not found`,
  );
  return valueFound.integrationProviderId;
}

/**
 * Converts a CollaborationUser type to GenericUserInfo type
 *
 * @param user The user to convert
 * @returns The corresponding GenericUserInfo or undefined if the user is null or undefined
 */
export function getGenericUserInfo(
  user: CollaborationUser | null | undefined,
): GenericUserInfo | undefined {
  if (!user) {
    return;
  }
  return {
    identity: user.id,
    email: user.email ?? "",
    name: user.name ?? undefined,
    authorityType: "UserRef",
  };
}

/** This array contains the known error codes that the Core API could return to the BCF backend */
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const KNOWN_BCF_BACKEND_CORE_API_ERROR_CODES = [401, 403, 404];

/**
 * Adds a file to an external annotation as document reference.
 *
 * @param bcfApiClient - The BCF API client to use for the request.
 * @param integration - The BCF integration type.
 * @param projectId - The ID of the project.
 * @param topicId - The ID of the topic.
 * @param file - The file to add as a document reference.
 * @returns A promise that resolves to the document reference of the added file.
 */
export async function addDocumentReferenceToAnnotation(
  bcfApiClient: BcfServicesApiClient,
  integration: BcfServicesIntegrationType,
  projectId: string,
  topicId: string,
  file: File,
): Promise<GetTopicDocumentReference> {
  const projectDocument = await bcfApiClient.uploadDocumentToProject(
    integration,
    projectId,
    file,
  );
  return await bcfApiClient.addDocumentReferenceToTopic(
    integration,
    projectId,
    topicId,
    projectDocument.guid,
  );
}

/**
 * Temporary function that checks if the document API is implemented for the given BCF Services integration type.
 * Will be removed when all external annotation types support document references.
 *
 * @param integrationType The BCF Services integration type to check.
 * @returns True if the document API is implemented, false otherwise.
 */
export function isDocumentApiImplemented(
  integrationType: BcfServicesIntegrationType,
): boolean {
  return (
    integrationType === BcfServicesIntegrationType.autodeskAccIssues ||
    integrationType === BcfServicesIntegrationType.autodeskAccRfis ||
    integrationType === BcfServicesIntegrationType.procoreObservations ||
    integrationType === BcfServicesIntegrationType.procoreRfis
  );
}

/** Map of external annotation error types to their corresponding error data */
export const EXTERNAL_ANNOTATION_ERROR: Record<
  ExternalAnnotationErrorType,
  Pick<
    ExternalAnnotationError,
    "title" | "color" | "shouldShowProviderAuthorization"
  >
> = {
  userTokenMissing: {
    title: "Authorization required",
    color: orange[400],
    shouldShowProviderAuthorization: true,
  },
  sessionMightHaveExpired: {
    title: "Session might have expired",
    color: orange[400],
    shouldShowProviderAuthorization: true,
  },
  projectIsDisconnected: {
    title: "Project is disconnected",
    color: orange[400],
    shouldShowProviderAuthorization: false,
  },
  connectionError: {
    title: "Connection error",
    color: red[400],
    shouldShowProviderAuthorization: false,
  },
};
